import React, { useEffect, useState } from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";

// The "is anything broken?" strip (born from the 2026-08-16 feed scare: an
// 11-hour quiet feed that looked like an outage and was a weekend).
//
// Each stat is a rolling 24h count against the SAME 24h window a week ago —
// the only fair baseline for an app with heavy weekday/weekend seasonality.
// The verdict chip does the interpretation so nobody has to: quiet-but-
// normal reads green, a real anomaly reads red.

const ageHours = (iso, nowIso) => {
  if (!iso) return null;
  const ms = new Date(nowIso || Date.now()) - new Date(iso);
  return Math.max(0, ms / 36e5);
};

const fmtAge = (h) =>
  h == null ? "never" : h < 1 ? `${Math.round(h * 60)}m ago` : `${Math.round(h)}h ago`;

function Stat({ label, cur, prev }) {
  const delta = prev > 0 ? ((cur - prev) / prev) * 100 : null;
  const down = delta != null && delta < -30;
  return (
    <Box sx={{ minWidth: 130 }}>
      <Typography variant="caption" color="text.secondary">
        {label} · 24h
      </Typography>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="h5" fontWeight={700}>
          {cur}
        </Typography>
        <Tooltip title="Same 24h window last week">
          <Typography
            variant="body2"
            sx={{ color: down ? "#c62828" : "text.secondary" }}
          >
            vs {prev}
            {delta != null && ` (${delta >= 0 ? "+" : ""}${Math.round(delta)}%)`}
          </Typography>
        </Tooltip>
      </Stack>
    </Box>
  );
}

export default function PulseStrip() {
  const [pulse, setPulse] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await AxiosInstance.get(ApiList.ADMIN_PULSE);
        if (alive && res?.data?.s === 1) setPulse(res.data.r);
        else if (alive) setError(true);
      } catch {
        if (alive) setError(true);
      }
    };
    load();
    const t = setInterval(load, 120000); // refresh every 2 min while open
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (error) return null; // the dashboard must not look broken over a side-dish
  if (!pulse) return null;

  const postAge = ageHours(pulse.last_post_at, pulse.db_now);
  const actAge = ageHours(pulse.last_activity_at, pulse.db_now);

  // Verdict: red = activity has genuinely stopped (no completed activity in
  // 12h — this app never sleeps that long, weekends included); amber = one
  // signal sags hard vs last week; green otherwise.
  const sag = (o) => o.prevWeek > 0 && o.last24h < o.prevWeek * 0.4;
  const red = actAge != null && actAge > 12;
  const amber = !red && (sag(pulse.activities) || sag(pulse.posts));
  const verdict = red
    ? { label: "CHECK THE API — activity has stopped", color: "#c62828", bg: "#fdecea" }
    : amber
    ? { label: "Quieter than last week", color: "#8a6d00", bg: "#fff8e1" }
    : { label: "All normal", color: "#1d3010", bg: "#eaf2d8" };

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 2.5,
        p: 2,
        mx: 2,
        mt: 2,
        bgcolor: "#fff",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 1.5, md: 4 }}
        alignItems={{ md: "center" }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 170 }}>
          <MonitorHeartIcon sx={{ color: verdict.color }} />
          <Box>
            <Typography fontWeight={700}>Pulse</Typography>
            <Chip
              label={verdict.label}
              size="small"
              sx={{ bgcolor: verdict.bg, color: verdict.color, fontWeight: 700 }}
            />
          </Box>
        </Stack>
        <Stat
          label="Activities"
          cur={pulse.activities.last24h}
          prev={pulse.activities.prevWeek}
        />
        <Stat label="Feed posts" cur={pulse.posts.last24h} prev={pulse.posts.prevWeek} />
        <Stat
          label="Active users"
          cur={pulse.active_users.last24h}
          prev={pulse.active_users.prevWeek}
        />
        <Box>
          <Typography variant="caption" color="text.secondary">
            Latest
          </Typography>
          <Typography variant="body2">
            activity {fmtAge(actAge)} · post {fmtAge(postAge)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
